import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { resetBusy, subscribeToBusy } from './busy';
import './global-loader.css';

/**
 * The app's one loading overlay.
 *
 * Mounted once, driven entirely by the axios interceptor — no page
 * raises it and no page needs to know it exists. What it covers is
 * whatever the reader was looking at when they acted, which is the
 * point: an action that changes what the page means should not leave
 * the page usable while it runs.
 *
 * Two timings matter, and both exist to stop the overlay being worse
 * than no overlay:
 *
 *   A DELAY before showing. Most requests here finish in well under
 *   200ms, and a veil that appears and vanishes inside that reads as a
 *   flicker — the interface twitching rather than working. Nothing is
 *   shown until the wait is long enough to be worth reporting.
 *
 *   A MINIMUM once shown. Having decided to cover the screen, taking it
 *   away 20ms later is the same flicker with an extra step. Once it is
 *   up it stays up long enough to be read as a state.
 *
 * Whether the veil is up is tracked in a REF, and every timer is started
 * and cleared here, in the listener. It used to start the show timer
 * inside a setVisible(updater) function. React runs an updater later, at
 * render time -- and twice under StrictMode -- so a request that settled
 * quickly had already run the hide branch (finding no timer to cancel)
 * before the updater created the show timer; that timer then raised the
 * veil 220ms later with nothing left to take it down. StrictMode's second
 * run leaked a second timer the same way. The veil stayed up for good.
 */

const SHOW_AFTER_MS = 220;
const MIN_VISIBLE_MS = 320;

/* The longest any request here is allowed to run is 45s (the schedule and
   report calls; the default is shorter). Busy for longer than this can only
   be a count that was never released -- a request that neither resolved nor
   rejected -- so the counter is reset rather than the app being left behind
   a veil for good. */
const STUCK_AFTER_MS = 60000;

const GlobalLoader = () => {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const stuckTimer = useRef(null);
  const shownAt = useRef(0);

  useEffect(() => {
    const clearTimer = (ref) => {
      clearTimeout(ref.current);
      ref.current = null;
    };

    const show = () => {
      visibleRef.current = true;
      shownAt.current = Date.now();
      setVisible(true);
    };

    const hide = () => {
      visibleRef.current = false;
      setVisible(false);
    };

    const unsubscribe = subscribeToBusy((busy) => {
      if (busy) {
        clearTimer(hideTimer);
        if (!stuckTimer.current) {
          stuckTimer.current = setTimeout(() => {
            stuckTimer.current = null;
            console.warn('GlobalLoader: busy for over 60s with nothing settling; resetting the overlay.');
            resetBusy();
          }, STUCK_AFTER_MS);
        }
        // Already up, or already counting down to being up.
        if (visibleRef.current || showTimer.current) return;
        showTimer.current = setTimeout(() => {
          showTimer.current = null;
          show();
        }, SHOW_AFTER_MS);
        return;
      }

      clearTimer(stuckTimer);

      // Settled before the delay elapsed: nothing was ever shown, so
      // there is nothing to take away.
      if (showTimer.current) {
        clearTimer(showTimer);
        return;
      }
      if (!visibleRef.current) return;

      const held = Date.now() - shownAt.current;
      clearTimer(hideTimer);
      hideTimer.current = setTimeout(() => {
        hideTimer.current = null;
        hide();
      }, Math.max(0, MIN_VISIBLE_MS - held));
    });

    return () => {
      unsubscribe();
      clearTimer(showTimer);
      clearTimer(hideTimer);
      clearTimer(stuckTimer);
    };
  }, []);

  if (!visible || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="gl-veil"
      /* Announced, not described: a screen reader is told the app is
         working without being handed a live region that fires on every
         save. aria-busy is what that state is called. */
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="gl-box">
        <span className="gl-spinner" aria-hidden="true" />
        <span className="gl-text">Working…</span>
      </div>
    </div>,
    document.body,
  );
};

export default GlobalLoader;
